import { Component, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormulairesService } from '../formulaires.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

const TYPE_PREFIXES: Record<string, string> = {
  REFE:  'FRMTT_FORM_REF_',
  CLUB:  'FRMTT_FORM_CL_',
  COACH: 'FRMTT_FORM_CO_',
  COMP:  'FRMTT_FORM_CP_',
  MANA:  'FRMTT_FORM_MA_',
  PLAY:  'FRMTT_FORM_PL_',
};

@Component({
  selector: 'app-formulaires-form',
  standalone: false,
  templateUrl: './formulaires-form.component.html',
  styleUrls: ['./formulaires-form.component.scss'],
})
export class FormulairesFormComponent implements OnDestroy {
  isLoading = signal(false);
  isLoadingCode = signal(false);
  errorMessage = signal<string | null>(null);
  nomFr = signal('');
  nomAr = signal('');
  type = signal('');
  code = signal('');
  pendingPdf = signal<File | null>(null);
  pendingDocx = signal<File | null>(null);

  readonly typeOptions = [
    { value: 'PLAY',  label: 'Joueurs' },
    { value: 'COACH', label: 'Entraîneurs' },
    { value: 'MANA',  label: 'Dirigeants' },
    { value: 'CLUB',  label: 'Clubs' },
    { value: 'COMP',  label: 'Compétitions' },
    { value: 'REFE',  label: 'Arbitres' },
  ];

  private destroy$ = new Subject<void>();

  constructor(private service: FormulairesService, private router: Router) {}

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  onTypeChange(value: string): void {
    this.type.set(value);
    this.code.set('');
    if (!value) return;

    const prefix = TYPE_PREFIXES[value];
    if (!prefix) return;

    this.isLoadingCode.set(true);
    this.service.getNextCode(prefix).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.code.set(res.code || ''); this.isLoadingCode.set(false); },
      error: () => this.isLoadingCode.set(false),
    });
  }

  onFileSelect(type: 'pdf' | 'docx', event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    type === 'pdf' ? this.pendingPdf.set(file) : this.pendingDocx.set(file);
    input.value = '';
  }

  clearFile(type: 'pdf' | 'docx'): void {
    type === 'pdf' ? this.pendingPdf.set(null) : this.pendingDocx.set(null);
  }

  async onSave(): Promise<void> {
    if (!this.nomFr().trim()) { this.errorMessage.set('Le nom français est requis'); return; }
    if (!this.type()) { this.errorMessage.set('Le type est requis'); return; }
    if (!this.code()) { this.errorMessage.set('Le code est requis — sélectionnez un type'); return; }

    const confirmed = await SwalUtils.confirmGreen('Créer le formulaire', 'Voulez-vous créer ce formulaire ?', 'Créer');
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const form = new FormData();
    form.append('formulaire_nom_fr', this.nomFr().trim());
    form.append('formulaire_nom_ar', this.nomAr().trim());
    form.append('formulaire_type', this.type());
    form.append('formulaire_code', this.code());
    if (this.pendingPdf()) form.append('files', this.pendingPdf()!);
    if (this.pendingDocx()) form.append('files', this.pendingDocx()!);

    this.service.createFormulaire(form).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        SwalUtils.success('Formulaire créé', 'Le formulaire a été créé avec succès.');
        if (res?.data?.formulaire_id) {
          this.router.navigate(['/federation/formulaires', res.data.formulaire_id]);
        } else {
          this.router.navigate(['/federation/formulaires']);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de la création.');
      }
    });
  }

  onCancel(): void { this.router.navigate(['/federation/formulaires']); }
}
